import React from "react";
import type { HoldTone } from "@client/lib/foscam/ptzHold";

/**
 * Prstenec kolem PTZ tlačítka — ukazuje, že držení je registrované.
 *
 * Během držení se plní (podíl bezpečnostního stropu), po puštění odpočítává dojezd
 * obrazu. Je to jediná zpětná vazba, kterou uživatel v prvních ~6 s má, protože
 * obraz je zpožděný.
 *
 * `strokeDashoffset` se řídí INLINE STYLEM z `progress`, ne CSS animací. Důvod:
 * `index.css` globálně nuluje `animation-duration` pod `prefers-reduced-motion`,
 * takže CSS-animovaný prstenec by uživatelům s omezeným pohybem nefungoval vůbec.
 */

const R = 16;
const CIRCUMFERENCE = 2 * Math.PI * R;

const TONE_STROKE: Record<HoldTone, string> = {
  idle: "transparent",
  pending: "var(--muted-foreground)",
  active: "var(--primary)",
  settle: "var(--status-info)",
  danger: "var(--destructive)",
};

interface Props {
  /** 0..1 */
  progress: number;
  tone: HoldTone;
  className?: string;
}

export const HoldRing: React.FC<Props> = ({ progress, tone, className }) => {
  if (tone === "idle") return null;
  const clamped = Math.min(1, Math.max(0, progress));

  return (
    <svg
      viewBox="0 0 36 36"
      aria-hidden="true"
      // `-inset-1` = prstenec obepíná tlačítko o 4 px venku, aby nepřekrýval ikonu.
      // `-rotate-90` posouvá start plnění na dvanáctou hodinu.
      className={`pointer-events-none absolute -inset-1 -rotate-90 ${className ?? ""}`}
    >
      {/* Podklad — bez něj není poznat, kolik z kruhu ještě zbývá. */}
      <circle cx="18" cy="18" r={R} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="2.5" />
      <circle
        cx="18"
        cy="18"
        r={R}
        fill="none"
        stroke={TONE_STROKE[tone]}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        style={{
          strokeDashoffset: CIRCUMFERENCE * (1 - clamped),
          // Krátký transition drží prstenec plynulý i mezi rAF snímky, ale je tak
          // krátký, že neschovává skutečný průběh.
          transition: "stroke-dashoffset 80ms linear",
        }}
      />
    </svg>
  );
};

export default HoldRing;
